"use client";

import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";
import {
    getLocalizedPath,
} from "@/lib/localizedRoutes";

const zones = [
    {
        name: "NEW YORK",
        image: "/v4/strike-zone-new-york.png",
    },
    {
        name: "TOKYO",
        image: "/v4/strike-zone-tokyo.png",
    },
    {
        name: "LONDON",
        image: "/v4/strike-zone-london.png",
    },
    {
        name: "BERLIN",
        image: "/v4/strike-zone-berlin.png",
    },
    {
        name: "DUBAI",
        image: "/v4/strike-zone-dubai.png",
    },
    {
        name: "PEKING",
        image: "/v4/strike-zone-peking.png",
    },
    {
        name: "EL DORADO",
        image: "/v4/strike-zone-el-dorado.png",
    },
    {
        name: "MEXICO CITY",
        image: "/v4/strike-zone-mexico-city.png",
    },
];

const points = [
    10,
    25,
    50,
    75,
    100,
    150,
];

const translations = {
    hu: {
        introduction: (
            <>
                a strike zone a játék fő pályája,{" "}
                <br className="md:hidden" />
                ahol nyolc különböző város vár rád{" "}
                <br className="md:hidden" />
                nyolc egyedi kihívással.{" "}
                <br />
                minden pályán hat dobásod van,{" "}
                <br className="md:hidden" />
                hogy minél több pontot gyűjts.{" "}
                <br />
                egy célfelület csak az első találatért ad pontot,{" "}
                <br />
                ugyanazt a pontot még egyszer nem szerezheted meg.{" "}
                <br />
                a cél egyszerű: érd el a lehető{" "}
                <br className="md:hidden" />
                legmagasabb pontszámot,{" "}
                <br className="md:hidden" />
                és nyisd meg a titkos bónuszpályát.
            </>
        ),

        pointZones:
            "pontzónák:",

        point:
            "pont",

        mainImageAlt:
            "strike zone interaktív ügyességi pálya Budapesten",

        reachedPoints:
            "elérted az 1500 pontot?",

        opens:
            "megnyílik a",
    },

    en: {
        introduction: (
            <>
                the strike zone is the main area of the game,{" "}
                <br className="md:hidden" />
                where eight different cities await you{" "}
                <br className="md:hidden" />
                with eight unique challenges.{" "}
                <br />
                you have six throws on each course{" "}
                <br className="md:hidden" />
                to collect as many points as possible.{" "}
                <br />
                each target awards points only for the first hit,{" "}
                <br />
                so you cannot score from the same target twice.{" "}
                <br />
                the goal is simple: achieve the{" "}
                <br className="md:hidden" />
                highest score possible{" "}
                <br className="md:hidden" />
                and unlock the secret bonus game.
            </>
        ),

        pointZones:
            "point zones:",

        point:
            "points",

        mainImageAlt:
            "strike zone interactive skill course in Budapest",

        reachedPoints:
            "have you reached 1500 points?",

        opens:
            "unlock",
    },
};

export default function StrikeZonePage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    const strikeZoneImage =
        language === "en"
            ? "/v4/talald_meg_eldoradot-en.webp"
            : "/v4/találd_meg_eldoradot.webp";

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            {/* BEMUTATKOZÁS */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex min-h-[505px] w-full max-w-[430px] flex-col items-center px-7 pb-12 pt-14 text-center md:max-w-6xl md:min-h-[570px] md:justify-center md:px-12 md:py-20">
                    <h1 className="text-[31px] font-black uppercase leading-none tracking-[-0.025em] md:text-[58px]">
                        STRIKE ZONE
                    </h1>

                    <div className="mt-7 w-full max-w-3xl rounded-[8px] border-2 border-black bg-white p-5 text-left md:p-7">
                        <h2 className="text-center text-[24px] font-black md:text-[30px]">
                            {language === "en" ? "how does it work?" : "hogyan működik?"}
                        </h2>
                        <ol className="mt-5 grid gap-4 md:grid-cols-2">
                            {(language === "en" ? ["choose a time slot.", "arrive 15 minutes early.", "complete all 8 courses.", "collect points and compete."] : ["válassz időpontot.", "érkezz 15 perccel korábban.", "teljesítsd a 8 pályát.", "gyűjts pontokat és versenyezz."]).map((step, index) => (
                                <li key={step} className="flex items-center gap-3 text-[15px] font-bold md:text-[17px]">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f5a400] font-black">{index + 1}</span>{step}
                                </li>
                            ))}
                        </ol>
                        <Link href="/booking" className="button-pulse mx-auto mt-6 flex min-h-[50px] max-w-xs items-center justify-center rounded-[6px] bg-[#f5a400] px-6 text-[19px] font-black text-black transition hover:scale-[1.02]">
                            {language === "en" ? "book now" : "foglalj most"}
                        </Link>
                    </div>

                    <p className="mt-9 max-w-[340px] text-[17px] leading-[1.08] text-neutral-800 md:max-w-3xl md:text-[25px] md:leading-[1.25]">
                        {text.introduction}
                    </p>

                    <h2 className="mt-10 text-[30px] font-black leading-none md:text-[46px]">
                        {text.pointZones}
                    </h2>

                    <div className="mt-7 grid grid-cols-3 gap-x-5 gap-y-4 md:grid-cols-6 md:gap-7">
                        {points.map((point) => (
                            <div
                                key={point}
                                className="flex h-[58px] w-[58px] flex-col items-center justify-center rounded-full border-[3px] border-neutral-700 bg-transparent text-center md:h-[82px] md:w-[82px]"
                            >
                                <strong className="text-[20px] font-black leading-[0.8] md:text-[29px]">
                                    {point}
                                </strong>

                                <span className="mt-1 text-[9px] font-black uppercase leading-none md:text-[12px]">
                                    {text.point}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NAGY STRIKE ZONE KÉP */}
            <section className="relative overflow-hidden bg-black">

                {/* BLUR HÁTTÉR */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            `url("${strikeZoneImage}")`,

                        backgroundSize:
                            "cover",

                        backgroundPosition:
                            "center",

                        filter:
                            "blur(40px)",

                        transform:
                            "scale(1.15)",

                        opacity:
                            0.7,
                    }}
                />

                {/* ÉLES KÉP */}
                <div
                    className="relative mx-auto"
                    style={{
                        width:
                            "100%",

                        maxWidth:
                            "1100px",
                    }}
                >
                    <img
                        src={
                            strikeZoneImage
                        }
                        alt={
                            text.mainImageAlt
                        }
                        className="block h-auto w-full"
                    />
                </div>
            </section>

            {/* VÁROSOK */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex w-full max-w-[430px] flex-col items-center px-10 pb-14 pt-12 md:max-w-6xl md:px-12 md:py-20">
                    <div className="grid w-full max-w-[282px] grid-cols-1 gap-[21px] md:max-w-5xl md:grid-cols-2 md:gap-7">
                        {zones.map((zone) => (
                            <a
                                key={
                                    zone.name
                                }
                                href={`#${zone.name
                                    .toLowerCase()
                                    .replaceAll(
                                        " ",
                                        "-"
                                    )}`}
                                className="group relative block h-[58px] overflow-hidden rounded-[7px] border border-neutral-500 bg-neutral-700 md:h-[105px]"
                            >
                                <img
                                    src={
                                        zone.image
                                    }
                                    alt={
                                        zone.name
                                    }
                                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                />

                                <div className="absolute inset-0 bg-black/20 transition group-hover:bg-black/10" />

                                <span className="absolute inset-0 flex items-center justify-center text-[21px] font-black uppercase leading-none text-white drop-shadow-md md:text-[35px]">
                                    {zone.name}
                                </span>
                            </a>
                        ))}
                    </div>

                    <div className="mt-12 text-center md:mt-16">
                        <p className="text-[17px] leading-[1.2] text-neutral-800 md:text-[25px]">
                            {text.reachedPoints}
                            <br />
                            {text.opens}
                        </p>

                        <a
                            href={getLocalizedPath(
                                "/feed-the-beast",
                                language
                            )}
                            className="mt-5 flex h-[67px] w-[282px] items-center justify-center overflow-hidden rounded-[6px] bg-black px-5 transition hover:scale-[1.02] md:h-[100px] md:w-[420px]"
                        >
                            <img
                                src="/v4/feed-the-beast-logo.png"
                                alt="Feed the Beast"
                                className="h-auto max-h-[54px] w-full object-contain md:max-h-[78px]"
                            />
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}